import { fetchWithAuth, readJsonSafe } from "./api";
import { reportConnection } from "./connectionSignal";

/**
 * The saves that wait for signal (orders overhaul Phase 4 — the technician's
 * offline mode): labor done, parts used, marking labor done, engine hours,
 * notes, clock in / out / switch.
 *
 * sendSave() keeps a save here, in the browser's IndexedDB, and sends it now
 * (kept first, so a page closed or reloaded mid-try still has it). With no
 * answer — no signal, Chariot timing out or restarting — it stays and is sent
 * again by itself: at once when the device is back online or the page comes
 * back to the front ("Send now" too), else on a backoff up to every five
 * minutes. Saves go out in the order they were made; one waiting holds back
 * the ones after it, so a clock-out never lands before its clock-in. Every
 * try of a save carries the same Idempotency-Key, and Chariot answers a key
 * it has seen with its first answer (chariot-backend middleware/idempotency.js):
 * a try that got through but whose answer was lost never makes a second line.
 * A body's SENT_AT is the device's clock as each try leaves (a clock tapped
 * with no signal says how long ago, by the device's own clock).
 *
 * A save can use what an earlier one made: a "{{<its id>}}" in its path is
 * replaced by the id the earlier save's answer gave (the clock-out of a
 * clock-in made with no signal). An answer that trying again won't change (a
 * 4xx other than signing in again) stops that save as failed, with Chariot's
 * reason, until the person tries again or removes it; the saves after it
 * still go, except those that needed its answer. Chariot answering "trouble"
 * (5xx) ten times running stops one the same way.
 *
 * Each save is its person's: on a shared iPad the next person to sign in
 * never sends — or sees — someone else's (setOutboxOwner, from AdminLayout);
 * signing out first sends what it can (flushOutbox). Two Chariot tabs share
 * the outbox without sending a save twice (a Web Lock, taken over from a tab
 * asleep in the background after a minute; each pass re-reads the stored
 * saves). Where IndexedDB isn't available the outbox still works while the
 * page stays open (each save says whether it was stored).
 *
 * States: waiting → sending → (gone, sent) | failed.
 */

const DB_NAME = "chariot-outbox";
const SAVES = "saves";
const ANSWERS = "answers"; // the id each sent save's answer gave, a day
const LOCK = "chariot-outbox";
const BACKOFF_MS = [15000, 30000, 60000, 120000, 300000];
const ANSWER_KEEP_MS = 24 * 60 * 60 * 1000;
const TRY_TIMEOUT_MS = 20000;
// A save sent as it's made is kept first; for this long no other tab (nor the
// page after a reload) sends it — a try, a sign-in renewal and the try again
// take at most about this.
const DIRECT_MS = 60000;
const MAX_SERVER_ERRORS = 10;
const LOCK_LOOK_AGAIN_MS = 5000;
const LOCK_TAKE_OVER_MS = 60000;
const REF_RE = /\{\{([0-9a-f-]{36})\}\}/gi;
const HAS_REF = /\{\{[0-9a-f-]{36}\}\}/i;
/** In a save's body: the device's clock (ms) as the try leaves. */
export const SENT_AT = "{{sent-at}}";
const SENT_AT_RE = /"\{\{sent-at\}\}"/g;

let saves = [];
let owner = null;
let started = false;
let running = null; // the pass under way (with any asked for meanwhile)
let pumpAgain = false;
let wakeTimer = null;
let lockWaitSince = 0;
let directTail = Promise.resolve(); // saves sent as they're made go one after another
const inFlight = new Set(); // ids this page is sending as they're made
const answers = new Map(); // save id → the id its answer gave
// The order saves were made in: milliseconds and a counter, so two made in
// the same millisecond keep their order (and a reload's come after).
let lastSeq = 0;
const nextSeq = () => { lastSeq = Math.max(lastSeq + 1, Date.now() * 1000); return lastSeq; };
const listeners = new Set();
const sentListeners = new Set();

// ── IndexedDB (never throws; null when unavailable) ─────────────────────────
let dbPromise = null;
function openDb() {
  if (!dbPromise) {
    const opening = new Promise((resolve) => {
      try {
        if (typeof indexedDB === "undefined") { resolve(null); return; }
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
          req.result.createObjectStore(SAVES, { keyPath: "id" });
          req.result.createObjectStore(ANSWERS, { keyPath: "id" });
        };
        req.onsuccess = () => {
          const db = req.result;
          // iOS closes the connection while Safari sits in the background: let go, open anew next time.
          const lose = () => { if (dbPromise === opening) dbPromise = null; };
          db.onclose = lose;
          db.onversionchange = () => { try { db.close(); } catch { /* fine */ } lose(); };
          resolve(db);
        };
        // Not kept as the answer: the next save tries to open it again.
        const failed = () => { if (dbPromise === opening) dbPromise = null; resolve(null); };
        req.onerror = failed;
        req.onblocked = failed;
      } catch {
        resolve(null);
      }
    });
    dbPromise = opening;
  }
  return dbPromise;
}

async function idb(store, mode, run, again = true) {
  const db = await openDb();
  if (!db) return { ok: false };
  const result = await new Promise((resolve) => {
    try {
      const tx = db.transaction(store, mode);
      const req = run(tx.objectStore(store));
      tx.oncomplete = () => resolve({ ok: true, value: req ? req.result : undefined });
      tx.onerror = () => resolve({ ok: false });
      tx.onabort = () => resolve({ ok: false });
    } catch {
      resolve({ ok: false });
    }
  });
  if (result.ok || !again) return result;
  if (dbPromise && (await dbPromise) === db) {
    try { db.close(); } catch { /* fine */ }
    dbPromise = null;
  }
  return idb(store, mode, run, false);
}

const stored = (s) => ({
  id: s.id, seq: s.seq, owner: s.owner, orderId: s.orderId, kind: s.kind, label: s.label,
  method: s.method, path: s.path, body: s.body, createdAt: s.createdAt,
  state: s.state === "failed" ? "failed" : "waiting", error: s.error || null, tries: s.tries || 0,
  serverErrors: s.serverErrors || 0, direct: s.direct || null,
});
const keep = (s) => idb(SAVES, "readwrite", (st) => st.put(stored(s)));
const drop = (id) => idb(SAVES, "readwrite", (st) => st.delete(id));

// ── State and listeners ─────────────────────────────────────────────────────
const mine = (s) => owner && s.owner === owner;
const byAge = (a, b) => (a.seq || a.createdAt * 1000) - (b.seq || b.createdAt * 1000);
// Being sent as it was made — by this page, another tab, or a page closed a moment ago: not "waiting".
const sendingNow = (s) => inFlight.has(s.id) || (s.direct && Date.now() - s.direct < DIRECT_MS);
const ordered = () => saves.filter(mine).sort(byAge);
const snapshot = () => ordered().filter((s) => !sendingNow(s)).map((s) => ({ ...s }));
function notify() {
  const list = snapshot();
  listeners.forEach((fn) => { try { fn(list); } catch { /* a listener's problem */ } });
}

// Picks up saves kept by another tab (or before a reload); lets go of ones
// another tab sent.
async function reconcile() {
  const { ok, value: rows } = await idb(SAVES, "readonly", (st) => st.getAll());
  if (!ok || !Array.isArray(rows)) return;
  const ids = new Set(rows.map((r) => r.id));
  let changed = false;
  for (const r of rows) {
    if (!saves.some((s) => s.id === r.id)) {
      saves.push({ ...r, nextTryAt: 0, persisted: true });
      changed = true;
    }
  }
  const before = saves.length;
  saves = saves.filter((s) => !s.persisted || ids.has(s.id) || s.state === "sending");
  if (changed || saves.length !== before) notify();
  const kept = await idb(ANSWERS, "readonly", (st) => st.getAll());
  if (kept.ok && Array.isArray(kept.value)) {
    const old = [];
    for (const a of kept.value) {
      if (Date.now() - a.at < ANSWER_KEEP_MS) answers.set(a.id, a.answerId);
      else old.push(a.id);
    }
    if (old.length) await idb(ANSWERS, "readwrite", (st) => { old.forEach((id) => st.delete(id)); return null; });
  }
}

// ── Sending ─────────────────────────────────────────────────────────────────
function wakeIn(ms) {
  if (wakeTimer) clearTimeout(wakeTimer);
  wakeTimer = setTimeout(() => { wakeTimer = null; pump(); }, Math.max(1000, ms));
}

// "{{id}}" in a path or body: the id an earlier save's answer gave; SENT_AT: now.
function fill(save) {
  const needs = new Set();
  const swap = (text) => text.replace(REF_RE, (whole, id) => {
    needs.add(id.toLowerCase());
    return answers.get(id.toLowerCase()) || whole;
  });
  const path = swap(save.path);
  const body = save.body === undefined ? undefined : swap(JSON.stringify(save.body)).replace(SENT_AT_RE, String(Date.now()));
  const missing = [...needs].filter((id) => !answers.has(id));
  return { path, body, missing };
}

// No answer, or one that says "again later": the network, a timeout, Chariot
// restarting or busy, a save of the same key still running.
const tryLater = (status, json) => status === 0 || status === 408 || status === 429 || status >= 500 || (status === 409 && json?.retry === true);
const answerIdOf = (json) => json?.id || json?.active?.id || null;

// Each try also says how the signal is (utils/connectionSignal.js): an answer
// of any kind is signal; none, or fetchWithAuth's own timeout (504), is not.
async function attempt(path, method, body, key) {
  const started = Date.now();
  try {
    const res = await fetchWithAuth(path, {
      method,
      body,
      headers: { "Idempotency-Key": key },
      skipSaveIndicator: true,
      timeout: TRY_TIMEOUT_MS,
    });
    reportConnection(res.status !== 504, Date.now() - started);
    return { res, json: await readJsonSafe(res) };
  } catch {
    reportConnection(false);
    return { res: null, json: null }; // no signal
  }
}

// The id its answer gave, for saves after it that need it.
async function remember(save, json) {
  const answerId = answerIdOf(json);
  if (!answerId) return;
  answers.set(save.id, answerId);
  await idb(ANSWERS, "readwrite", (st) => st.put({ id: save.id, answerId, at: Date.now() }));
}

async function forget(id) {
  await drop(id);
  saves = saves.filter((s) => s.id !== id);
}

async function sent(save, json) {
  await remember(save, json);
  await forget(save.id);
  notify();
  sentListeners.forEach((fn) => { try { fn(save, json); } catch { /* fine */ } });
}

function backoff(save) {
  save.state = "waiting";
  save.tries = (save.tries || 0) + 1;
  save.nextTryAt = Date.now() + BACKOFF_MS[Math.min(save.tries - 1, BACKOFF_MS.length - 1)];
}

async function sendOne(save) {
  const filled = fill(save);
  if (filled.missing.length) {
    // What it needs comes from a save still waiting — or one that failed, which it can't go without.
    const needsFailed = saves.some((s) => filled.missing.includes(s.id) && s.state === "failed");
    const needsGone = !saves.some((s) => filled.missing.includes(s.id));
    if (needsFailed || needsGone) {
      save.state = "failed";
      save.error = "The save it follows didn't go through.";
      await keep(save);
      notify();
      return "next";
    }
    return "wait";
  }
  save.state = "sending";
  notify();
  const { res, json } = await attempt(filled.path, save.method, filled.body, save.id);
  if (res?.ok) {
    await sent(save, json);
    return "next";
  }
  const status = res?.status || 0;
  save.serverErrors = status >= 500 ? (save.serverErrors || 0) + 1 : 0;
  // (A 401 the sign-in couldn't renew: it waits for the next sign-in.)
  if ((tryLater(status, json) || status === 401) && save.serverErrors < MAX_SERVER_ERRORS) {
    backoff(save);
    await keep(save);
    notify();
    return "wait";
  }
  save.state = "failed";
  save.error = json?.error || (status >= 500 ? "Chariot had trouble with this save." : `Chariot didn't take it (${status}).`);
  await keep(save);
  notify();
  return "next";
}

async function pass() {
  await reconcile();
  for (const live of ordered()) {
    if (live.state === "failed" || !saves.includes(live)) continue;
    // Being sent as it was made: this page's finishes the pass; another tab's, or a closed page's, a minute.
    if (inFlight.has(live.id)) return;
    if (live.direct && Date.now() - live.direct < DIRECT_MS) { wakeIn(live.direct + DIRECT_MS - Date.now()); return; }
    if ((live.nextTryAt || 0) > Date.now()) { wakeIn(live.nextTryAt - Date.now()); return; }
    const outcome = await sendOne(live); // one at a time, in order
    if (outcome === "wait") {
      if (live.nextTryAt) wakeIn(live.nextTryAt - Date.now());
      return;
    }
  }
}

/** Sends what waits, in order; resolves when done (a pass asked for meanwhile runs too). */
export function pump() {
  if (!owner) return Promise.resolve();
  if (running) { pumpAgain = true; return running; }
  running = (async () => {
    try {
      do {
        pumpAgain = false;
        if (typeof navigator !== "undefined" && navigator.locks?.request) {
          const got = await navigator.locks.request(LOCK, { ifAvailable: true }, async (lock) => {
            if (!lock) return false;
            await pass();
            return true;
          });
          if (got) {
            lockWaitSince = 0;
          } else if (!lockWaitSince || Date.now() - lockWaitSince < LOCK_TAKE_OVER_MS) {
            // Another tab is sending: look again shortly.
            if (!lockWaitSince) lockWaitSince = Date.now();
            wakeIn(LOCK_LOOK_AGAIN_MS);
          } else {
            // Held a minute — by a tab asleep in the background: take it (a save both send still runs once).
            lockWaitSince = 0;
            await navigator.locks.request(LOCK, { steal: true }, () => pass());
          }
        } else {
          await pass();
        }
      } while (pumpAgain && owner);
    } finally {
      running = null;
    }
  })();
  return running;
}

// Back online, back in front, "Send now": what waits goes now, not at its next backoff.
function wake() {
  saves.forEach((s) => { if (mine(s) && s.state === "waiting") s.nextTryAt = 0; });
  return pump();
}

/** "Send now". */
export const sendNow = () => wake();

/** How many of the signed-in person's saves are still on the device. */
export const outboxCount = () => ordered().length;

/** Before signing out: sends what it can now; → how many saves are still on the device. */
export async function flushOutbox(timeoutMs = 8000) {
  if (!owner) return 0;
  await Promise.race([wake(), new Promise((resolve) => { setTimeout(resolve, timeoutMs); })]);
  return ordered().length;
}

// ── The page's side ─────────────────────────────────────────────────────────

/** Starts the outbox once (AdminLayout): what was kept goes out when it can. */
export function startOutbox() {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("online", () => wake());
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") wake(); });
  reconcile().then(() => pump());
}

/** The signed-in person (AdminLayout); null when signed out. */
export function setOutboxOwner(userId) {
  owner = userId ? String(userId) : null;
  notify();
  if (owner) pump();
}

/**
 * A save: kept, and sent now — or when it can.
 *   { method, path, body, kind, label, orderId, extra }
 *   kind / label / orderId say what it is (the outbox list, the work view's waiting lines)
 *   extra: a promise of more body fields that take a moment (a clock's GPS
 *     fix, up to 10 s): the save is kept first — a page closed or reloaded
 *     meanwhile still has it and sends it without them — then sent with them
 * → { ok: true, res, json }          sent and taken
 *   { ok: false, res, json }         sent and refused (show json.error)
 *   { queued: true, id }             kept: it goes when it can
 */
export async function sendSave({ method, path, body, kind, label, orderId, extra }) {
  if (!owner) return { ok: false, res: null, json: { error: "Sign in again to save." } };
  const save = {
    id: crypto.randomUUID(), seq: nextSeq(), owner, orderId: orderId ? String(orderId) : null, kind, label, method, path, body,
    createdAt: Date.now(), state: "waiting", tries: 0, nextTryAt: 0, direct: Date.now(),
  };
  saves.push(save);
  inFlight.add(save.id);
  save.persisted = (await keep(save)).ok;
  // After the saves made before it that are being sent the same way (in order, one at a time).
  const before = directTail;
  let done;
  directTail = new Promise((resolve) => { done = resolve; });
  try {
    await before;
    if (extra) {
      try { save.body = { ...(save.body || {}), ...((await extra) || {}) }; } catch { /* sent without them */ }
      save.persisted = (await keep(save)).ok;
    }
    // Behind saves still waiting, in order — and never sent for someone who signed out meanwhile.
    const behind = saves.some((s) => s !== save && mine(s) && s.state !== "failed" && s.seq < save.seq);
    if (save.owner !== owner || behind || HAS_REF.test(path)) return await queue(save);
    const filled = fill(save);
    const { res, json } = await attempt(filled.path, method, filled.body, save.id);
    if (res && !tryLater(res.status, json) && res.status !== 401) {
      if (res.ok) await remember(save, json);
      await forget(save.id);
      // Saves another tab kept behind it go on.
      if (ordered().length) pump();
      return { ok: res.ok, res, json };
    }
    if ((res?.status || 0) >= 500) save.serverErrors = 1;
    backoff(save);
    return await queue(save);
  } finally {
    inFlight.delete(save.id);
    done();
  }
}

// Not sent as it was made: it waits its turn like any kept save.
async function queue(save) {
  inFlight.delete(save.id);
  save.direct = null;
  save.state = "waiting";
  save.persisted = (await keep(save)).ok;
  notify();
  pump();
  return { queued: true, id: save.id };
}

/** "{{id}}": in a later save's path, the id this save's answer gives. */
export const answerOf = (id) => `{{${id}}}`;

export function subscribeOutbox(fn) {
  listeners.add(fn);
  fn(snapshot());
  return () => listeners.delete(fn);
}

/** Called with (save, json) when a kept save has gone. */
export function onOutboxSent(fn) {
  sentListeners.add(fn);
  return () => sentListeners.delete(fn);
}

/**
 * Called once with [{ save, json }, …] for a run of kept saves going out one
 * after another (none for `waitMs` after the last): one reload of the page
 * for the lot, not one per save.
 */
export function onOutboxSentBatch(fn, waitMs = 1000) {
  let batch = [];
  let timer = null;
  const off = onOutboxSent((save, json) => {
    batch.push({ save, json });
    clearTimeout(timer);
    timer = setTimeout(() => {
      const list = batch;
      batch = [];
      timer = null;
      fn(list);
    }, waitMs);
  });
  return () => { off(); clearTimeout(timer); };
}

export async function retrySave(id) {
  const s = saves.find((x) => x.id === id && mine(x));
  if (!s) return;
  s.state = "waiting";
  s.error = null;
  s.nextTryAt = 0;
  s.serverErrors = 0;
  await keep(s);
  notify();
  pump();
}

export async function removeSave(id) {
  if (!saves.some((x) => x.id === id && mine(x))) return;
  await drop(id);
  saves = saves.filter((x) => x.id !== id);
  notify();
}
